import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { GoogleMap, Marker, InfoWindow, OverlayView } from "@react-google-maps/api";
import busIcon from "../../../assets/images/icons/BusImg.png";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { Button, CircularProgress, Typography, Box, Card } from "@mui/material";

import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

const containerStyle = {
  width: "100%",
  height: "70vh",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
};

const FALLBACK_CENTER = { lat: 15.283877, lng: 76.411995 };
const FALLBACK_ZOOM = 8;

const VehicleLocationMap = ({ vehicles = [], onRefresh }) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("TOTAL");
  const [isLoading, setIsLoading] = useState(true);

  const mapRef = useRef(null);
  const boundsAppliedRef = useRef(false);

  const hasMany = vehicles.length > 1;

  /** ---------------- MAP LOAD ---------------- */
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  /** ---------------- FIT BOUNDS AFTER VEHICLES LOAD ---------------- */
  useEffect(() => {
    if (!mapRef.current || !vehicles.length || !hasMany) return;
    if (boundsAppliedRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    vehicles.forEach(({ latitude, longitude }) => {
      bounds.extend({ lat: latitude, lng: longitude });
    });

    mapRef.current.fitBounds(bounds);
    boundsAppliedRef.current = true;
  }, [vehicles, hasMany]);

  /** ---------------- STOP LOADER AFTER VEHICLES LOAD ---------------- */
  useEffect(() => {
    setIsLoading(false);
    boundsAppliedRef.current = false;
  }, [vehicles]);

  /** ---------------- REFRESH ---------------- */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    boundsAppliedRef.current = false;

    try {
      await onRefresh();
    } catch (err) {
      console.error("Refresh failed:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  /** ---------------- FILTER VEHICLES ---------------- */
  const filteredVehicles = useMemo(() => {
    if (selectedStatus === "MOVING") {
      return vehicles.filter((v) => Number(v.speed) > 0);
    }
    if (selectedStatus === "STOPPED") {
      return vehicles.filter((v) => Number(v.speed) === 0);
    }
    return vehicles; // TOTAL
  }, [vehicles, selectedStatus]);

  /** ---------------- MARKERS ---------------- */
  const markers = useMemo(() => {
    if (!filteredVehicles.length) return null;

    return filteredVehicles.map((vehicle) => (
      <React.Fragment key={vehicle.chassisNumber}>
        <Marker
          position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
          icon={{
            url: busIcon,
            scaledSize: new window.google.maps.Size(42, 42),
          }}
          onClick={() => setSelectedVehicle(vehicle)}
        />

        {vehicle.alias_number && (
          <OverlayView
            position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                transform: "translate(-50%, -120%)",
                fontSize: "12px",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {vehicle.alias_number}
            </div>
          </OverlayView>
        )}
      </React.Fragment>
    ));
  }, [filteredVehicles]);

  /** ---------------- STATS ---------------- */
  const stats = useMemo(() => {
    const total = vehicles.length;
    const moving = vehicles.filter((v) => Number(v.speed) > 0).length;
    const stopped = total - moving;

    return { total, moving, stopped };
  }, [vehicles]);

  return (
    <div style={{ position: "relative" }}>
      {/* GOOGLE MAP */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={FALLBACK_CENTER}
        zoom={FALLBACK_ZOOM}
        onLoad={onLoad}
        options={{
          gestureHandling: "greedy",
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {markers}

        {selectedVehicle && (
          <InfoWindow
            position={{
              lat: selectedVehicle.latitude,
              lng: selectedVehicle.longitude,
            }}
            onCloseClick={() => setSelectedVehicle(null)}
          >
            <div style={{ minWidth: "180px" }}>
              <h4>{selectedVehicle.vehicle_number}</h4>
              <p>
                <b>Alias:</b> {selectedVehicle.alias_number}
              </p>
              <p>
                <b>Speed:</b> {selectedVehicle.speed} km/h
              </p>
              <p>
                <b>Address:</b> {selectedVehicle.address}
              </p>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* LOADING OVERLAY */}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.6)",
            zIndex: 10,
          }}
        >
          <CircularProgress />
          <Typography mt={1} fontSize={14}>
            Loading vehicle locations...
          </Typography>
        </div>
      )}

      {/* NO VEHICLES MESSAGE */}
      {!isLoading && vehicles.length === 0 && (
        <Typography
          mt={2}
          fontSize={14}
          textAlign="center"
          sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
        >
          No vehicles found.
        </Typography>
      )}

      {/* STATUS CARDS */}
      {vehicles.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 2,
            zIndex: 20,
          }}
        >
          {/* TOTAL */}
          <Card
            onClick={() => setSelectedStatus("TOTAL")}
            sx={statusCard("#1976d2", selectedStatus === "TOTAL")}
          >
            <DirectionsBusIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6">{stats.total}</Typography>
            <Typography variant="caption">Total</Typography>
          </Card>

          {/* STOPPED */}
          <Card
            onClick={() => setSelectedStatus("STOPPED")}
            sx={statusCard("#f57c00", selectedStatus === "STOPPED")}
          >
            <PauseCircleIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6">{stats.stopped}</Typography>
            <Typography variant="caption">Stopped</Typography>
          </Card>

          {/* MOVING */}
          <Card
            onClick={() => setSelectedStatus("MOVING")}
            sx={statusCard("#2e7d32", selectedStatus === "MOVING")}
          >
            <PlayCircleIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6">{stats.moving}</Typography>
            <Typography variant="caption">Moving</Typography>
          </Card>
        </Box>
      )}

      {/* REFRESH BUTTON */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "50%",
          background: "#fff",
          borderRadius: "6px",
          zIndex: 11,
        }}
      >
        <Button
          variant="outlined"
          startIcon={isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>
    </div>
  );
};

const statusCard = (bgColor, selected) => ({
  minWidth: 90,
  px: 2,
  py: 1.2,
  borderRadius: 2,
  backgroundColor: bgColor,
  color: "#fff",
  textAlign: "center",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.5,
  boxShadow: selected
    ? "0 0 0 3px #fff, 0 10px 25px rgba(0,0,0,0.35)"
    : "0 8px 20px rgba(0,0,0,0.25)",
  transform: selected ? "scale(1.05)" : "scale(1)",
  transition: "all 0.2s ease",
});

export default VehicleLocationMap;
